import { supabase } from "@/integrations/supabase/client";
import { notifyWaitlistSlotAvailable } from "@/utils/adminNotifications";

/**
 * Booking context that callers can pass to avoid a direct SELECT from bookings
 * (which fails under anon RLS in customer-facing pages).
 *
 * For admin mode, the service fetches this automatically.
 * For customer mode, the caller must provide it from already-loaded data.
 */
interface BookingContext {
  businessId?: string;
  serviceId?: string;
  bookingDate?: string;
  bookingTime?: string;
}

/**
 * Extended context fetched in admin mode — includes fields needed for
 * Google Calendar sync (which is admin-only since it needs the business
 * owner's OAuth tokens via JWT).
 */
interface AdminBookingContext extends BookingContext {
  googleCalendarEventId?: string;
  customerName?: string;
  customerEmail?: string;
  serviceName?: string;
  duration?: number;
  businessTimezone?: string;
}

interface CancelBookingParams {
  bookingId: string;
  reason?: string;
  mode: "admin" | "customer";
  /** Pre-loaded booking context. Required for customer mode (anon can't SELECT). */
  context?: BookingContext;
  /** Manage-link slug. Required for customer mode — authorizes the mutation. */
  slug?: string;
}

interface RescheduleBookingParams {
  bookingId: string;
  /**
   * INVARIANT: newDatetime must encode the business-local wall-clock time as
   * UTC components via Date.UTC(year, month, day, hours, minutes). The Z suffix
   * in the resulting ISO string is misleading — it does NOT represent actual UTC.
   * This convention is required because the DB RPC casts p_new_datetime::DATE
   * and ::TIME to extract naive local components.
   *
   * Example: user selects 2:00 PM → Date.UTC(2024, 6, 4, 14, 0) → "2024-07-04T14:00:00.000Z"
   * The RPC stores booking_date = 2024-07-04, booking_time = 14:00:00.
   * syncCalendarForReschedule extracts getUTCHours() = 14, pairs with business timezone.
   *
   * Passing an actual UTC timestamp (e.g. from Date.now()) will store the wrong
   * local time and create the calendar event at the wrong hour.
   */
  newDatetime: string;
  mode: "admin" | "customer";
  /** Pre-loaded booking context. Required for customer mode (anon can't SELECT). */
  context?: BookingContext;
  /** Manage-link slug. Required for customer mode — authorizes the mutation. */
  slug?: string;
}

interface BookingActionResult {
  success: boolean;
  error?: string;
  data?: Record<string, unknown>;
  calendarWarning?: string;
}

/**
 * Fetch full booking context for admin side effects. Includes calendar event ID,
 * customer details, and business timezone for Google Calendar sync.
 * Only works for authenticated users (admin mode).
 */
async function fetchAdminContext(bookingId: string): Promise<AdminBookingContext> {
  const { data } = await supabase
    .from("bookings")
    .select(
      "google_calendar_event_id, business_id, service_id, booking_date, booking_time, customer_name, customer_email, service_name, duration, business_profiles(timezone)"
    )
    .eq("id", bookingId)
    .single();

  const profile = data?.business_profiles as { timezone: string | null } | null;

  return {
    businessId: data?.business_id ?? undefined,
    serviceId: data?.service_id ?? undefined,
    bookingDate: data?.booking_date ?? undefined,
    bookingTime: data?.booking_time ?? undefined,
    googleCalendarEventId: data?.google_calendar_event_id ?? undefined,
    customerName: data?.customer_name ?? undefined,
    customerEmail: data?.customer_email ?? undefined,
    serviceName: data?.service_name ?? undefined,
    duration: data?.duration ?? undefined,
    businessTimezone: profile?.timezone ?? undefined,
  };
}

/**
 * Cancel a booking via the appropriate RPC, then fire all side effects.
 *
 * - admin mode: uses admin_cancel_booking (ownership-validated, no booking link needed).
 *   Also deletes Google Calendar event (requires business owner's OAuth tokens).
 * - customer mode: uses cancel_booking_via_link (requires valid booking link).
 *   No calendar sync — customer has no access to business owner's Google Calendar.
 */
export async function cancelBooking({
  bookingId,
  reason,
  mode,
  context,
  slug,
}: CancelBookingParams): Promise<BookingActionResult> {
  // In admin mode, fetch full context (incl. calendar). In customer mode, rely on caller.
  const adminCtx = mode === "admin" ? await fetchAdminContext(bookingId) : null;
  const ctx: BookingContext = context || adminCtx || {};

  // Call the appropriate RPC
  let rpcResult: { success: boolean; error?: string; data?: Record<string, unknown> };

  if (mode === "admin") {
    const { data, error } = await supabase.rpc("admin_cancel_booking", {
      p_booking_id: bookingId,
      p_cancellation_reason: reason || null,
    });
    if (error) return { success: false, error: error.message };
    rpcResult = data as { success: boolean; error?: string; data?: Record<string, unknown> };
  } else {
    const { data, error } = await supabase.rpc("cancel_booking_via_link", {
      p_booking_id: bookingId,
      p_cancellation_reason: reason || "Cancelled by client",
      p_slug: slug,
    });
    if (error) return { success: false, error: error.message };
    rpcResult = data as { success: boolean; error?: string; data?: Record<string, unknown> };
  }

  if (!rpcResult?.success) {
    return { success: false, error: rpcResult?.error || "Failed to cancel booking" };
  }

  // --- Side effects (all non-blocking) ---

  const businessId = (rpcResult.data?.business_id as string) || ctx.businessId;
  const serviceId = (rpcResult.data?.service_id as string) || ctx.serviceId;
  const bookingDate = (rpcResult.data?.booking_date as string) || ctx.bookingDate;
  const bookingTime = (rpcResult.data?.booking_time as string) || ctx.bookingTime;

  // 1. Cancellation email
  supabase.functions
    .invoke("booking-confirmation", {
      body: { booking_id: bookingId, email_type: "cancellation" },
    })
    .catch((e) => console.error("[cancelBooking] cancellation email failed:", e));

  // 2. Admin SMS notification (skip if admin-initiated — they already know)
  if (mode === "customer") {
    supabase.functions
      .invoke("notify-admin-booking", {
        body: { booking_id: bookingId, event_type: "cancellation" },
      })
      .catch((e) => console.error("[cancelBooking] admin SMS failed:", e));
  }

  // 3. Waitlist notification
  if (businessId && serviceId && bookingDate && bookingTime) {
    notifyWaitlistSlotAvailable({
      businessId,
      serviceId,
      availableDate: bookingDate,
      availableTime: bookingTime,
    }).catch((e) => console.error("[cancelBooking] waitlist notification failed:", e));
  }

  // 4. Google Calendar delete (admin-only — needs business owner's OAuth)
  if (mode === "admin" && adminCtx?.googleCalendarEventId) {
    supabase.functions
      .invoke("google-calendar-sync", {
        body: { action: "deleteEvent", eventId: adminCtx.googleCalendarEventId },
      })
      .catch((e) => console.error("[cancelBooking] calendar delete failed:", e));
  }

  return { success: true, data: rpcResult.data };
}

/**
 * Reschedule a booking via the appropriate RPC, then fire all side effects.
 *
 * - admin mode: uses admin_reschedule_booking (ownership-validated, skips cutoff).
 *   Google Calendar sync is awaited: old event deleted, new event created at
 *   business-local time, new event ID persisted. Returns calendarWarning if
 *   calendar sync fails but DB reschedule succeeded.
 * - customer mode: uses reschedule_booking_via_link (requires valid booking link).
 *   No calendar sync — customer has no access to business owner's Google Calendar.
 */
export async function rescheduleBooking({
  bookingId,
  newDatetime,
  mode,
  context,
  slug,
}: RescheduleBookingParams): Promise<BookingActionResult> {
  // In admin mode, fetch full context (incl. calendar). In customer mode, rely on caller.
  const adminCtx = mode === "admin" ? await fetchAdminContext(bookingId) : null;
  const ctx: BookingContext = context || adminCtx || {};

  // Call the appropriate RPC
  let rpcResult: { success: boolean; error?: string; data?: Record<string, unknown> };

  if (mode === "admin") {
    const { data, error } = await supabase.rpc("admin_reschedule_booking", {
      p_booking_id: bookingId,
      p_new_datetime: newDatetime,
    });
    if (error) return { success: false, error: error.message };
    rpcResult = data as { success: boolean; error?: string; data?: Record<string, unknown> };
  } else {
    const { data, error } = await supabase.rpc("reschedule_booking_via_link", {
      p_booking_id: bookingId,
      p_new_datetime: newDatetime,
      p_slug: slug,
    });
    if (error) return { success: false, error: error.message };
    rpcResult = data as { success: boolean; error?: string; data?: Record<string, unknown> };
  }

  if (!rpcResult?.success) {
    return { success: false, error: rpcResult?.error || "Failed to reschedule booking" };
  }

  // --- Non-blocking side effects ---

  const oldDate = (rpcResult.data?.old_date as string) || ctx.bookingDate;
  const oldTime = (rpcResult.data?.old_time as string) || ctx.bookingTime;
  const businessId = ctx.businessId;
  const serviceId = ctx.serviceId;

  // 1. Updated confirmation email
  supabase.functions
    .invoke("booking-confirmation", {
      body: { booking_id: bookingId },
    })
    .catch((e) => console.error("[rescheduleBooking] confirmation email failed:", e));

  // 2. Admin SMS notification about reschedule (skip if admin-initiated)
  if (mode === "customer") {
    supabase.functions
      .invoke("notify-admin-booking", {
        body: { booking_id: bookingId, event_type: "new_booking" },
      })
      .catch((e) => console.error("[rescheduleBooking] admin SMS failed:", e));
  }

  // 3. Waitlist: notify about the freed OLD slot
  if (businessId && serviceId && oldDate && oldTime) {
    notifyWaitlistSlotAvailable({
      businessId,
      serviceId,
      availableDate: oldDate,
      availableTime: oldTime,
    }).catch((e) => console.error("[rescheduleBooking] waitlist notification failed:", e));
  }

  // --- Awaited admin calendar sync ---
  let calendarWarning: string | undefined;

  if (mode === "admin" && adminCtx) {
    calendarWarning = await syncCalendarForReschedule(
      bookingId,
      newDatetime,
      adminCtx
    );
  }

  return { success: true, data: rpcResult.data, calendarWarning };
}

/**
 * Admin-only: delete old Google Calendar event, create replacement at the new
 * business-local time, persist the new event ID on the booking.
 *
 * Returns a warning string if calendar sync failed, or undefined on success/no-op.
 * Never throws — DB reschedule already succeeded.
 */
async function syncCalendarForReschedule(
  bookingId: string,
  newDatetime: string,
  ctx: AdminBookingContext
): Promise<string | undefined> {
  // Skip entirely if no calendar event existed (business may not have Calendar connected)
  if (!ctx.googleCalendarEventId) {
    return undefined;
  }

  // 1. Delete old event (tolerate failure — event may have been manually deleted)
  try {
    const { error } = await supabase.functions.invoke("google-calendar-sync", {
      body: { action: "deleteEvent", eventId: ctx.googleCalendarEventId },
    });
    if (error) {
      console.warn("[rescheduleBooking] old calendar event delete failed (non-fatal):", error);
    }
  } catch (e) {
    console.warn("[rescheduleBooking] old calendar event delete failed (non-fatal):", e);
  }

  // 2. Build new event using business-local date/time (not UTC Z-suffix).
  //    The newDatetime ISO string encodes the business-local clock time as UTC
  //    components (via Date.UTC in the caller). Extract those components.
  const dt = new Date(newDatetime);
  const newDate = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  const newTime = `${String(dt.getUTCHours()).padStart(2, "0")}:${String(dt.getUTCMinutes()).padStart(2, "0")}:00`;
  const durationMinutes = ctx.duration || 60;

  // Compute end time in the same wall-clock space
  const endTotalMinutes = dt.getUTCHours() * 60 + dt.getUTCMinutes() + durationMinutes;
  const endH = String(Math.floor(endTotalMinutes / 60) % 24).padStart(2, "0");
  const endM = String(endTotalMinutes % 60).padStart(2, "0");
  const endTime = `${endH}:${endM}:00`;

  // Use business timezone; fall back to America/New_York if not set
  const tz = ctx.businessTimezone || "America/New_York";

  // Google Calendar API accepts dateTime without Z when paired with timeZone
  const startDateTime = `${newDate}T${newTime}`;
  const endDateTime = `${newDate}T${endTime}`;

  try {
    const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
      body: {
        action: "createEvent",
        event: {
          summary: `${ctx.serviceName || "Appointment"} - ${ctx.customerName || "Client"}`,
          start: { dateTime: startDateTime, timeZone: tz },
          end: { dateTime: endDateTime, timeZone: tz },
          attendees: ctx.customerEmail ? [{ email: ctx.customerEmail }] : undefined,
        },
      },
    });

    if (error) {
      console.error("[rescheduleBooking] calendar create failed:", error);
      return "Booking rescheduled but failed to update Google Calendar";
    }

    // 3. Persist new event ID on the booking
    const newEventId = data?.eventId;
    if (newEventId) {
      await supabase
        .from("bookings")
        .update({ google_calendar_event_id: newEventId })
        .eq("id", bookingId);
    }

    return undefined; // success, no warning
  } catch (e) {
    console.error("[rescheduleBooking] calendar create failed:", e);
    return "Booking rescheduled but failed to update Google Calendar";
  }
}
